import React from "react";
import styled from "styled-components";

import { IWorkspaceResource, WorkspacePermissionSubjectType, WorkspaceResourceFolderType, WorkspaceResourceType } from "common/interfaces";
import type { PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, { getUserPermissionsToResource, getWorkspaceResources } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger(LogGroup.LIBRARY);

const FolderSectionHeader = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${themeColors.lighterGray};
    letter-spacing: 0.6px;
`;

const FolderSection = styled.div`
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TextParagraph = styled.p`
    margin-top: 0;
`;

const FolderList = styled.ul`
    margin: 0;
    padding-left: 20px;
`;

export async function handleUnshareFolderConflict({ collaboratedPresentations }: { collaboratedPresentations: PresentationLibraryPresentation[] }) {
    let workspaceResources: IWorkspaceResource[] = [];
    try {
        workspaceResources = await getWorkspaceResources({
            workspaceId: WorkspaceController.workspace.id,
            resourceTypes: [WorkspaceResourceType.PRESENTATION],
            resourceFilters: [{ field: "resourceId", operator: "in", value: collaboratedPresentations.map(p => p.id) }]
        });
    } catch (err) {
        logger.error(err, "[handleUnshareFolderConflict] failed to get workspace resources");
        ShowErrorDialog({
            title: "Unable to unshare presentation(s)",
            message: err.message,
        });
        return false;
    }

    const workspaceFolders = WorkspaceController.resourceFolders;
    const folderConflictData = collaboratedPresentations.reduce<{
        teamFolderById: Map<string, { id: string; name: string }>;
        personalFolderById: Map<string, { id: string; name: string }>;
    }>((acc, p) => {
        const resource = workspaceResources.find(
            r => r.resourceId === p.id && r.resourceType === WorkspaceResourceType.PRESENTATION
        );
        const folders = !resource?.folderIds?.length ? []
            : workspaceFolders
                .filter(f => resource.folderIds.includes(f.id))
                .map(f => ({ id: f.id, name: f.name, folderType: f.folderType }));

        // we do not want to allow users to leave SHARED_DEFAULT (all team members folder)
        const teamFolders = folders.filter(f => f.folderType === WorkspaceResourceFolderType.SHARED)
            .map(({ id, name }) => ({ id, name }));

        const personalFolders = folders.filter(f => f.folderType === WorkspaceResourceFolderType.PERSONAL)
            .map(({ id, name }) => ({ id, name }));

        for (const f of teamFolders) {
            acc.teamFolderById.set(f.id, f);
        }
        for (const f of personalFolders) {
            acc.personalFolderById.set(f.id, f);
        }

        return acc;
    }, {
        teamFolderById: new Map<string, { id: string; name: string }>(),
        personalFolderById: new Map<string, { id: string; name: string }>(),
    });

    const teamCount = folderConflictData.teamFolderById.size;
    const hasPersonal = folderConflictData.personalFolderById.size > 0;

    if (teamCount === 0 && !hasPersonal) return null;

    const totalFoldersCount = teamCount + folderConflictData.personalFolderById.size;
    const singleTeamFolder = folderConflictData.teamFolderById.values().next().value;
    const folderConflictDetailTitle = totalFoldersCount === 1 ? `Leave ${sanitizeHtmlText(singleTeamFolder.name)}`
        : "Leave folders to unshare this presentation";
    const folderConflictOkLabel = totalFoldersCount === 1 ? `Leave ${sanitizeHtmlText(singleTeamFolder.name)}` : "Leave folders";

    const folderConflictDetailMessage = (
        <>
            <TextParagraph>
                {totalFoldersCount === 1 ? `This presentation is shared with you through ${sanitizeHtmlText(singleTeamFolder.name)} folder. To remove your access, you'll need to leave the folder. Leaving will remove your access to all items shared through it.`
                    : `This presentation is shared with you through multiple folders. To remove your access, you'll need to leave all the folders listed below. Leaving will remove your access to all items shared through each folder.`}
            </TextParagraph>

            {totalFoldersCount > 1 && (<>
                <FolderSection>
                    <FolderSectionHeader>
                        FOLDERS SHARING THIS PRESENTATION
                    </FolderSectionHeader>
                    <FolderList>
                        {[...folderConflictData.teamFolderById.values()].map(f => (
                            <li key={f.id}>{sanitizeHtmlText(f.name)}</li>
                        ))}
                    </FolderList>
                </FolderSection>
                {hasPersonal && (
                    <FolderSection>
                        <FolderSectionHeader>
                            PERSONAL FOLDERS (REMOVE FROM FOLDER)
                        </FolderSectionHeader>
                        <FolderList>
                            {[...folderConflictData.personalFolderById.values()].map(f => (
                                <li key={f.id}>{sanitizeHtmlText(f.name)}</li>
                            ))}
                        </FolderList>
                    </FolderSection>
                )}
            </>)}
        </>
    );

    const confirmFolderSteps = await ShowConfirmationDialog({
        title: folderConflictDetailTitle,
        message: folderConflictDetailMessage,
        okButtonLabel: folderConflictOkLabel,
    });
    if (!confirmFolderSteps) {
        return true;
    }

    const removeCollaboratedPresentationsFromPersonalFolder = async (folderId: string) => {
        const resources = collaboratedPresentations
            .filter(p => {
                const wr = workspaceResources.find(
                    r => r.resourceId === p.id && r.resourceType === WorkspaceResourceType.PRESENTATION,
                );
                return wr?.folderIds?.includes(folderId);
            }).map(p => ({
                resourceId: p.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
            }));
        await WorkspaceController.updateWorkspaceResourceFolderResources({
            folderId,
            update: {
                remove: resources
            }
        });
    };

    try {
        await withProgressDialog({
            title: "Updating folders and finishing unshare...",
            action: async () => {
                for (const personalFolder of folderConflictData.personalFolderById.values()) {
                    await removeCollaboratedPresentationsFromPersonalFolder(personalFolder.id);
                }
                for (const teamFolder of folderConflictData.teamFolderById.values()) {
                    await WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: teamFolder.id,
                        update: {
                            remove: [
                                {
                                    subjectId: app.user.id,
                                    subjectType: WorkspacePermissionSubjectType.USER,
                                },
                            ],
                        },
                    });
                }
                // After folder updates, read access can disappear immediately. In that case, we treat "no access"
                // as success because the user has already been effectively unshared from those presentations.
                for (const { id: resourceId, workspaceId } of collaboratedPresentations) {
                    const permissions = await getUserPermissionsToResource({
                        workspaceId,
                        resourceId,
                        resourceType: WorkspaceResourceType.PRESENTATION,
                    });
                    if (!permissions.read.object) {
                        continue;
                    }

                    await WorkspaceController.updateWorkspaceResourceUsers({
                        resourceId,
                        resourceType: WorkspaceResourceType.PRESENTATION,
                        update: {
                            remove: [
                                {
                                    subjectId: app.user.id,
                                    subjectType: WorkspacePermissionSubjectType.USER,
                                },
                            ],
                        },
                    });
                }
            },
        });
    } catch (err) {
        logger.error(err, "[handleUnshareFolderConflict] unshare after folder updates failed");

        ShowErrorDialog({
            title: "Unable to unshare presentation(s)",
            message: err.message,
        });
    }

    return true;
}
